export const MANAGED_TABLE_KEYS = [
  'forms',
  'formEntries',
  'timeEntries',
  'invoices',
  'subscriptions',
  'payments',
  'agents',
  'calls',
  'prompts',
] as const;

export type ManagedTableKey = (typeof MANAGED_TABLE_KEYS)[number];

export type ManagedTableGroup = 'Clients' | 'Work' | 'Billing' | 'AI Systems';

export type ManagedTableColumnType =
  | 'text'
  | 'status'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'number'
  | 'url'
  | 'boolean'
  | 'longText'
  | 'image'
  | 'attachment'
  | 'phone'
  | 'email'
  | 'rating'
  | 'duration'
  | 'audioUrl'
  | 'relation'
  | 'richText';

export type ManagedTableDetailGroup =
  | 'Overview'
  | 'Contact/Client'
  | 'Status'
  | 'Performance/Usage'
  | 'Media'
  | 'Prompt/Transcript/Summary'
  | 'Raw Fields';

export type ManagedTableCardRole =
  | 'media'
  | 'title'
  | 'subtitle'
  | 'badge'
  | 'meta'
  | 'hidden';

export interface ManagedTableColumn {
  id: string;
  label: string;
  sourceField: string;
  type?: ManagedTableColumnType;
  currencyUnit?: 'dollars' | 'cents';
  table?: boolean;
  card?: boolean;
  detail?: boolean;
  detailGroup?: ManagedTableDetailGroup;
  cardRole?: ManagedTableCardRole;
}

export type ManagedTableEditableFieldType =
  | 'text'
  | 'longText'
  | 'status'
  | 'boolean'
  | 'number'
  | 'linkedRecord';

export interface ManagedTableEditableField {
  id: string;
  label: string;
  sourceField: string;
  type: ManagedTableEditableFieldType;
  placeholder?: string;
}

export interface ManagedTableConfig {
  key: ManagedTableKey;
  title: string;
  description: string;
  group: ManagedTableGroup;
  route: string;
  tableName: string;
  /**
   * Airtable table reference passed to the Airtable REST client. The client
   * URL-encodes this value, and Airtable accepts EITHER a table id (`tbl…`) or
   * a URL-encoded table name here. We store the table NAME so managed tables
   * stay portable across duplicated bases (demo/staging), which get new table
   * ids but keep table names. Named `tableId` for historical reasons.
   */
  tableId: string;
  titleField: string;
  subtitleField?: string;
  statusField?: string;
  searchFields: string[];
  sort?: {
    field: string;
    direction: 'asc' | 'desc';
  };
  columns: ManagedTableColumn[];
  editableFields?: ManagedTableEditableField[];
  /** When true, records in this table may be deleted through the managed-table route. */
  deletable?: boolean;
}

export interface ManagedTableRecord {
  id: string;
  createdTime: string;
  fields: Record<string, unknown>;
}

export interface ManagedTableListResponse {
  table: ManagedTableConfig;
  items: ManagedTableRecord[];
  total: number;
}

export interface ManagedTableRecordResponse {
  table: ManagedTableConfig;
  item: ManagedTableRecord;
}

export const MANAGED_TABLE_CONFIGS: Record<ManagedTableKey, ManagedTableConfig> = {
  forms: {
    key: 'forms',
    title: 'Forms',
    description: 'Client and prospect form inventory, source pages, sync status, and related submissions.',
    group: 'Clients',
    route: '/clients/forms',
    tableName: 'EXT_Forms',
    tableId: 'EXT_Forms',
    titleField: 'form_name',
    subtitleField: 'source_site_name',
    searchFields: [
      'form_name',
      'source_site_name',
      'source_site_url',
      'default_page_url',
      'external_form_id',
    ],
    sort: { field: 'last_synced', direction: 'desc' },
    columns: [
      {
        id: 'formName',
        label: 'Form',
        sourceField: 'form_name',
        type: 'text',
        cardRole: 'title',
        detailGroup: 'Overview',
      },
      {
        id: 'formId',
        label: 'Form ID',
        sourceField: 'form_id',
        type: 'text',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'externalFormId',
        label: 'External Form ID',
        sourceField: 'external_form_id',
        type: 'text',
        detailGroup: 'Overview',
      },
      {
        id: 'organization',
        label: 'Organization',
        sourceField: 'org',
        type: 'relation',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'sourceSystem',
        label: 'Source',
        sourceField: 'source_system',
        type: 'status',
        detailGroup: 'Overview',
      },
      {
        id: 'sourceSiteUrl',
        label: 'Source Site URL',
        sourceField: 'source_site_url',
        type: 'url',
        table: false,
        detailGroup: 'Overview',
      },
      {
        id: 'sourceSiteName',
        label: 'Source Site',
        sourceField: 'source_site_name',
        type: 'text',
        cardRole: 'subtitle',
        detailGroup: 'Overview',
      },
      {
        id: 'defaultPageUrl',
        label: 'Page URL',
        sourceField: 'default_page_url',
        type: 'url',
        detailGroup: 'Overview',
      },
      {
        id: 'wpPostId',
        label: 'WP Post ID',
        sourceField: 'wp_post_id',
        type: 'number',
        table: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'purpose',
        label: 'Purpose',
        sourceField: 'form_purpose',
        type: 'status',
        detailGroup: 'Overview',
      },
      {
        id: 'active',
        label: 'Active',
        sourceField: 'is_active',
        type: 'boolean',
        detailGroup: 'Status',
      },
      {
        id: 'schemaJson',
        label: 'Schema JSON',
        sourceField: 'schema_json',
        type: 'longText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'standardFieldMap',
        label: 'Standard Field Map',
        sourceField: 'standard_field_map',
        type: 'longText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'formPreview',
        label: 'Form Preview',
        sourceField: 'Form Preview',
        type: 'richText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'formPreviewMirror',
        label: 'Form Preview Mirror',
        sourceField: 'FormPreviewmirror',
        type: 'richText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'displayConfig',
        label: 'Display Config',
        sourceField: 'display_config',
        type: 'longText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'lastSynced',
        label: 'Last Synced',
        sourceField: 'last_synced',
        type: 'date',
        detailGroup: 'Status',
      },
      {
        id: 'meta',
        label: 'Meta',
        sourceField: 'meta',
        type: 'longText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'entries',
        label: 'Entries',
        sourceField: 'EXT_FormEntries',
        type: 'relation',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
    ],
  },
  formEntries: {
    key: 'formEntries',
    title: 'Form Entries',
    description: 'Submitted form entries with contact details, AI summaries, and parsed submission fields.',
    group: 'Clients',
    route: '/clients/forms/entries',
    tableName: 'EXT_FormEntries',
    tableId: 'EXT_FormEntries',
    titleField: 'Full Name',
    subtitleField: 'Form Name',
    deletable: true,
    searchFields: [
      'Full Name',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Ai Entry Summary',
      'All Fields (JSON)',
      'Form Name',
      'Site Name',
    ],
    sort: { field: 'Created At', direction: 'desc' },
    columns: [
      {
        id: 'entryId',
        label: 'Entry ID',
        sourceField: 'Entry ID',
        type: 'text',
        table: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'organization',
        label: 'Organization',
        sourceField: 'Organization_link',
        type: 'relation',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'siteName',
        label: 'Site Name',
        sourceField: 'Site Name',
        type: 'text',
        table: false,
        detailGroup: 'Overview',
      },
      {
        id: 'submittedPageUrl',
        label: 'Submitted Page URL',
        sourceField: 'Submitted Page URL',
        type: 'url',
        table: false,
        detailGroup: 'Overview',
      },
      {
        id: 'submittedPageName',
        label: 'Submitted Page Name',
        sourceField: 'Submitted Page Name',
        type: 'text',
        table: false,
        detailGroup: 'Overview',
      },
      {
        id: 'siteUrl',
        label: 'Site URL',
        sourceField: 'Site URL',
        type: 'url',
        table: false,
        detailGroup: 'Overview',
      },
      {
        id: 'formId',
        label: 'Form ID',
        sourceField: 'Form ID',
        type: 'text',
        table: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'formName',
        label: 'Form',
        sourceField: 'Form Name',
        type: 'status',
        cardRole: 'subtitle',
        detailGroup: 'Overview',
      },
      {
        id: 'submittedAt',
        label: 'Submitted',
        sourceField: 'Submitted At',
        type: 'datetime',
        detailGroup: 'Overview',
      },
      {
        id: 'fullName',
        label: 'Name',
        sourceField: 'Full Name',
        type: 'text',
        cardRole: 'title',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'firstName',
        label: 'First Name',
        sourceField: 'First Name',
        type: 'text',
        table: false,
        detailGroup: 'Contact/Client',
      },
      {
        id: 'lastName',
        label: 'Last Name',
        sourceField: 'Last Name',
        type: 'text',
        table: false,
        detailGroup: 'Contact/Client',
      },
      {
        id: 'email',
        label: 'Email',
        sourceField: 'Email',
        type: 'email',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'phone',
        label: 'Phone',
        sourceField: 'Phone',
        type: 'phone',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'summary',
        label: 'AI Summary',
        sourceField: 'Ai Entry Summary',
        type: 'longText',
        detailGroup: 'Prompt/Transcript/Summary',
      },
      {
        id: 'allFieldsJson',
        label: 'All Fields JSON',
        sourceField: 'All Fields (JSON)',
        type: 'longText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'clientContact',
        label: 'Client Contact',
        sourceField: 'Client Contact',
        type: 'relation',
        table: false,
        detailGroup: 'Contact/Client',
      },
      {
        id: 'allFieldsFormatted',
        label: 'All Fields Formatted',
        sourceField: 'All Fields Formatted',
        type: 'richText',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'createdAt',
        label: 'Created At',
        sourceField: 'Created At',
        type: 'datetime',
        detailGroup: 'Overview',
      },
      {
        id: 'forms',
        label: 'Forms',
        sourceField: 'EXT_Forms',
        type: 'relation',
        table: false,
        card: false,
        detailGroup: 'Raw Fields',
      },
    ],
  },
  timeEntries: {
    key: 'timeEntries',
    title: 'Tracked Time',
    description: 'Time entry records for task billing, corrections, and quick-log cleanup.',
    group: 'Work',
    route: '/work/time-entries',
    tableName: 'Time Entries',
    tableId: 'Time Entries',
    titleField: 'Time Entry Name',
    subtitleField: 'Task Name (from Billable Tasks)',
    statusField: 'Status',
    deletable: true,
    searchFields: [
      'Time Entry Name',
      'Task Name (from Billable Tasks)',
      'Notes',
      'Status',
      'Source',
      'Client',
      'Billable Tasks',
    ],
    sort: { field: 'Entry Date', direction: 'desc' },
    columns: [
      { id: 'name', label: 'Name', sourceField: 'Time Entry Name', type: 'text' },
      { id: 'task', label: 'Task', sourceField: 'Task Name (from Billable Tasks)', type: 'text' },
      { id: 'status', label: 'Status', sourceField: 'Status', type: 'status' },
      { id: 'minutes', label: 'Minutes', sourceField: 'Manual Duration Minutes', type: 'number' },
      { id: 'billable', label: 'Billable', sourceField: 'Billable', type: 'boolean' },
      { id: 'entryDate', label: 'Entry Date', sourceField: 'Entry Date', type: 'date' },
      { id: 'source', label: 'Source', sourceField: 'Source', type: 'text' },
      { id: 'notes', label: 'Notes', sourceField: 'Notes', type: 'longText', card: false },
    ],
    editableFields: [
      { id: 'name', label: 'Name', sourceField: 'Time Entry Name', type: 'text' },
      { id: 'notes', label: 'Notes', sourceField: 'Notes', type: 'longText' },
      { id: 'status', label: 'Status', sourceField: 'Status', type: 'status' },
      {
        id: 'billable',
        label: 'Billable',
        sourceField: 'Billable',
        type: 'boolean',
        placeholder: 'Bill this time entry',
      },
      {
        id: 'client',
        label: 'Client',
        sourceField: 'Client',
        type: 'linkedRecord',
        placeholder: 'Airtable record id',
      },
      {
        id: 'billableTasks',
        label: 'Billable Tasks',
        sourceField: 'Billable Tasks',
        type: 'linkedRecord',
        placeholder: 'Airtable record id',
      },
      {
        id: 'durationMinutes',
        label: 'Manual Duration Minutes',
        sourceField: 'Manual Duration Minutes',
        type: 'number',
      },
    ],
  },
  invoices: {
    key: 'invoices',
    title: 'Invoices',
    description: 'Invoice status, payment progress, and Stripe review links.',
    group: 'Billing',
    route: '/billing/invoices',
    tableName: 'BILL_Invoices',
    tableId: 'BILL_Invoices',
    titleField: 'Invoice ID',
    subtitleField: 'Company Name (from Clients)',
    statusField: 'Invoice Status',
    searchFields: [
      'Invoice ID',
      'Company Name (from Clients)',
      'Invoice Status',
      'Stripe Invoice ID',
      'Stripe Invoice Number',
      'Client Email',
    ],
    sort: { field: 'Created Date', direction: 'desc' },
    columns: [
      { id: 'invoiceId', label: 'Invoice ID', sourceField: 'Invoice ID', type: 'text' },
      {
        id: 'company',
        label: 'Company',
        sourceField: 'Company Name (from Clients)',
        type: 'text',
      },
      { id: 'status', label: 'Status', sourceField: 'Invoice Status', type: 'status' },
      {
        id: 'amountDue',
        label: 'Amount Due',
        sourceField: 'Amount Due (raw)',
        type: 'currency',
        currencyUnit: 'cents',
      },
      {
        id: 'amountPaid',
        label: 'Amount Paid',
        sourceField: 'Amount Paid (raw)',
        type: 'currency',
        currencyUnit: 'cents',
      },
      { id: 'createdDate', label: 'Created', sourceField: 'Created Date', type: 'date' },
      { id: 'paidDate', label: 'Paid', sourceField: 'Paid Date', type: 'date' },
      { id: 'stripeUrl', label: 'Stripe URL', sourceField: 'Stripe Hosted URL', type: 'url' },
    ],
  },
  subscriptions: {
    key: 'subscriptions',
    title: 'Subscriptions',
    description: 'Recurring billing status, cadence, pricing, and active client services.',
    group: 'Billing',
    route: '/billing/subscriptions',
    tableName: 'BILL_Subscriptions',
    tableId: 'BILL_Subscriptions',
    titleField: 'Subscription ID',
    subtitleField: 'Client',
    statusField: 'Subscription Status',
    searchFields: [
      'Subscription ID',
      'Client',
      'Subscription Status',
      'Billing Category',
      'Stripe Subscription ID',
      'Email Address (from Contacts) (from Client)',
    ],
    sort: { field: 'Next Invoice Date', direction: 'asc' },
    columns: [
      { id: 'subscriptionId', label: 'Subscription ID', sourceField: 'Subscription ID', type: 'text' },
      { id: 'client', label: 'Client', sourceField: 'Client', type: 'text' },
      { id: 'status', label: 'Status', sourceField: 'Subscription Status', type: 'status' },
      { id: 'billingCategory', label: 'Category', sourceField: 'Billing Category', type: 'text' },
      { id: 'price', label: 'Price', sourceField: 'Price', type: 'currency' },
      { id: 'discountedPrice', label: 'Discounted', sourceField: 'price with discount', type: 'currency' },
      { id: 'interval', label: 'Interval', sourceField: 'Interval', type: 'text' },
      { id: 'nextInvoiceDate', label: 'Next Invoice', sourceField: 'Next Invoice Date', type: 'date' },
      {
        id: 'currentPeriodEnd',
        label: 'Period End',
        sourceField: 'Current Period End Friendly',
        type: 'text',
      },
    ],
  },
  payments: {
    key: 'payments',
    title: 'Payments',
    description: 'Payment ledger records for reconciliation across invoices and subscriptions.',
    group: 'Billing',
    route: '/billing/payments',
    tableName: 'BILL_Payments',
    tableId: 'BILL_Payments',
    titleField: 'Payment ID',
    subtitleField: 'Client',
    statusField: 'Status',
    searchFields: [
      'Payment ID',
      'Client',
      'Status',
      'Payment Platform',
      'Provider Payment ID',
      'Provider Invoice ID',
      'Payment Notes',
    ],
    sort: { field: 'Effective Date', direction: 'desc' },
    columns: [
      { id: 'paymentId', label: 'Payment ID', sourceField: 'Payment ID', type: 'text' },
      { id: 'client', label: 'Client', sourceField: 'Client', type: 'text' },
      { id: 'status', label: 'Status', sourceField: 'Status', type: 'status' },
      { id: 'direction', label: 'Direction', sourceField: 'Direction', type: 'text' },
      { id: 'platform', label: 'Platform', sourceField: 'Payment Platform', type: 'text' },
      { id: 'method', label: 'Method', sourceField: 'Payment Method', type: 'text' },
      { id: 'grossAmount', label: 'Gross', sourceField: 'Gross Amount', type: 'currency' },
      { id: 'feeAmount', label: 'Fees', sourceField: 'Fee Amount', type: 'currency' },
      { id: 'netAmount', label: 'Net', sourceField: 'Net Amount', type: 'currency' },
      { id: 'currency', label: 'Currency', sourceField: 'Currency', type: 'text' },
      { id: 'effectiveDate', label: 'Effective', sourceField: 'Effective Date', type: 'date' },
    ],
  },
  agents: {
    key: 'agents',
    title: 'Agents',
    description: 'AI agent profiles, connection status, visibility, and assigned client context.',
    group: 'AI Systems',
    route: '/ai-systems/agents',
    tableName: 'Ai_Agents',
    tableId: 'Ai_Agents',
    titleField: 'Agent Name',
    subtitleField: 'Company Name (from Assigned Client)',
    statusField: 'Agent Profile Status',
    searchFields: [
      'Agent Name',
      'Agent Profile Status',
      'Agent Type',
      'Company Name (from Assigned Client)',
      'Connection Status',
      'vapi_agent_id',
    ],
    sort: { field: 'Last Modified', direction: 'desc' },
    columns: [
      {
        id: 'avatar',
        label: 'Avatar',
        sourceField: 'Agent Avatar',
        type: 'image',
        table: false,
        cardRole: 'media',
        detailGroup: 'Media',
      },
      {
        id: 'agentName',
        label: 'Agent',
        sourceField: 'Agent Name',
        type: 'text',
        cardRole: 'title',
        detailGroup: 'Overview',
      },
      {
        id: 'company',
        label: 'Assigned Client',
        sourceField: 'Company Name (from Assigned Client)',
        type: 'relation',
        cardRole: 'subtitle',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'profileStatus',
        label: 'Profile Status',
        sourceField: 'Agent Profile Status',
        type: 'status',
        cardRole: 'badge',
        detailGroup: 'Status',
      },
      {
        id: 'connection',
        label: 'Connection',
        sourceField: 'Connection Status',
        type: 'status',
        detailGroup: 'Status',
      },
      {
        id: 'visibility',
        label: 'Visibility',
        sourceField: 'Visibility',
        type: 'status',
        detailGroup: 'Status',
      },
      {
        id: 'agentType',
        label: 'Agent Type',
        sourceField: 'Agent Type',
        type: 'relation',
        detailGroup: 'Overview',
      },
      {
        id: 'agentRole',
        label: 'Agent Role',
        sourceField: 'Agent Role',
        type: 'text',
        detailGroup: 'Overview',
      },
      {
        id: 'voiceType',
        label: 'Voice Type',
        sourceField: 'Voice Type',
        type: 'status',
        detailGroup: 'Overview',
      },
      {
        id: 'personaStatus',
        label: 'Persona Status',
        sourceField: 'Persona Status',
        type: 'status',
        table: false,
        detailGroup: 'Status',
      },
      {
        id: 'avatarStatus',
        label: 'Avatar Status',
        sourceField: 'Avatar Status',
        type: 'status',
        table: false,
        detailGroup: 'Status',
      },
      {
        id: 'phone',
        label: 'Phone',
        sourceField: 'Agent Phone Number',
        type: 'phone',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'vapiAgentId',
        label: 'Vapi Agent ID',
        sourceField: 'vapi_agent_id',
        type: 'text',
        table: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'vapiDashboard',
        label: 'Vapi Dashboard',
        sourceField: 'Vapi Dashboard Link',
        type: 'url',
        table: false,
        detailGroup: 'Raw Fields',
      },
      {
        id: 'usageMinutes',
        label: 'Usage Minutes',
        sourceField: 'sumMinutes',
        type: 'duration',
        detailGroup: 'Performance/Usage',
      },
      {
        id: 'totalCost',
        label: 'Total Cost',
        sourceField: 'total_cost Rollup (from Ai_Calls)',
        type: 'currency',
        detailGroup: 'Performance/Usage',
      },
      {
        id: 'promptStatus',
        label: 'Prompt Status',
        sourceField: 'Prompt Status',
        type: 'status',
        table: false,
        detailGroup: 'Prompt/Transcript/Summary',
      },
      {
        id: 'promptBuild',
        label: 'Prompt Build',
        sourceField: 'Prompt Build V2',
        type: 'richText',
        table: false,
        card: false,
        detailGroup: 'Prompt/Transcript/Summary',
      },
      {
        id: 'introClip',
        label: 'Intro Clip',
        sourceField: 'Agent Intro Clip',
        type: 'attachment',
        table: false,
        detailGroup: 'Media',
      },
      {
        id: 'lastModified',
        label: 'Last Modified',
        sourceField: 'Last Modified',
        type: 'datetime',
        detailGroup: 'Raw Fields',
      },
    ],
  },
  calls: {
    key: 'calls',
    title: 'Calls',
    description: 'AI call history, caller details, outcomes, duration, and cost.',
    group: 'AI Systems',
    route: '/ai-systems/calls',
    tableName: 'Ai_Calls',
    tableId: 'Ai_Calls',
    titleField: 'call_id',
    subtitleField: 'caller_full_name',
    statusField: 'Status',
    searchFields: [
      'call_id',
      'caller_full_name',
      'business_name',
      'phone_number',
      'email_address',
      'Status',
      'call_summary',
      'reason_for_call',
    ],
    sort: { field: 'started_at', direction: 'desc' },
    columns: [
      {
        id: 'agentAvatar',
        label: 'Agent Avatar',
        sourceField: 'Agent Avatar Lookup',
        type: 'image',
        table: false,
        cardRole: 'media',
        detailGroup: 'Media',
      },
      {
        id: 'callId',
        label: 'Call ID',
        sourceField: 'call_id',
        type: 'text',
        cardRole: 'title',
        detailGroup: 'Overview',
      },
      {
        id: 'caller',
        label: 'Caller',
        sourceField: 'caller_full_name',
        type: 'text',
        cardRole: 'subtitle',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'business',
        label: 'Business',
        sourceField: 'business_name',
        type: 'text',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'phone',
        label: 'Phone',
        sourceField: 'phone_number',
        type: 'phone',
        detailGroup: 'Contact/Client',
      },
      {
        id: 'email',
        label: 'Email',
        sourceField: 'email_address',
        type: 'email',
        table: false,
        detailGroup: 'Contact/Client',
      },
      {
        id: 'status',
        label: 'Status',
        sourceField: 'Status',
        type: 'status',
        cardRole: 'badge',
        detailGroup: 'Status',
      },
      {
        id: 'type',
        label: 'Call Type',
        sourceField: 'Type Of Call',
        type: 'status',
        detailGroup: 'Overview',
      },
      {
        id: 'startedAt',
        label: 'Started',
        sourceField: 'started_at',
        type: 'datetime',
        detailGroup: 'Overview',
      },
      {
        id: 'endedAt',
        label: 'Ended',
        sourceField: 'ended_at',
        type: 'datetime',
        table: false,
        detailGroup: 'Overview',
      },
      {
        id: 'duration',
        label: 'Duration',
        sourceField: 'duration_minutes',
        type: 'duration',
        detailGroup: 'Performance/Usage',
      },
      {
        id: 'cost',
        label: 'Cost',
        sourceField: 'total_cost',
        type: 'currency',
        detailGroup: 'Performance/Usage',
      },
      {
        id: 'rating',
        label: 'AI Rating',
        sourceField: 'aiRating',
        type: 'rating',
        detailGroup: 'Performance/Usage',
      },
      {
        id: 'ratingReason',
        label: 'AI Rating Reason',
        sourceField: 'aiRatingReason',
        type: 'longText',
        table: false,
        detailGroup: 'Performance/Usage',
      },
      {
        id: 'endedReason',
        label: 'Ended Reason',
        sourceField: 'ended_reason',
        type: 'status',
        detailGroup: 'Status',
      },
      {
        id: 'agent',
        label: 'Agent',
        sourceField: 'Vapi Agent_1st',
        type: 'relation',
        detailGroup: 'Overview',
      },
      {
        id: 'recording',
        label: 'Recording',
        sourceField: 'call_recording_url',
        type: 'audioUrl',
        detailGroup: 'Media',
      },
      {
        id: 'reason',
        label: 'Reason For Call',
        sourceField: 'reason_for_call',
        type: 'text',
        table: false,
        detailGroup: 'Prompt/Transcript/Summary',
      },
      {
        id: 'summary',
        label: 'Summary',
        sourceField: 'call_summary',
        type: 'richText',
        table: false,
        card: false,
        detailGroup: 'Prompt/Transcript/Summary',
      },
      {
        id: 'transcript',
        label: 'Transcript',
        sourceField: 'call_trascript',
        type: 'richText',
        table: false,
        card: false,
        detailGroup: 'Prompt/Transcript/Summary',
      },
      {
        id: 'urgency',
        label: 'Urgency',
        sourceField: 'urgency',
        type: 'text',
        table: false,
        detailGroup: 'Status',
      },
      {
        id: 'requestedRecap',
        label: 'Requested Recap',
        sourceField: 'caller_requested_recap',
        type: 'boolean',
        table: false,
        detailGroup: 'Status',
      },
      {
        id: 'saved',
        label: 'Saved Call',
        sourceField: 'Saved Call List',
        type: 'boolean',
        table: false,
        detailGroup: 'Status',
      },
      {
        id: 'spam',
        label: 'Spam',
        sourceField: 'MarkSpam',
        type: 'boolean',
        table: false,
        detailGroup: 'Status',
      },
    ],
  },
  prompts: {
    key: 'prompts',
    title: 'Prompts',
    description: 'Prompt library records, versions, purpose, and build strategy.',
    group: 'AI Systems',
    route: '/ai-systems/prompts',
    tableName: 'Ai_Prompts',
    tableId: 'Ai_Prompts',
    titleField: 'Prompt Template Name + Version',
    subtitleField: 'Purpose of Prompt',
    statusField: 'Status',
    searchFields: [
      'Prompt Template Name + Version',
      'Prompt Record ID',
      'Status',
      'Purpose of Prompt',
      'Prompt Type',
      'Constructed Prompt',
      'Static Prompt',
    ],
    sort: { field: 'Version Number', direction: 'desc' },
    columns: [
      {
        id: 'promptName',
        label: 'Prompt',
        sourceField: 'Prompt Template Name + Version',
        type: 'text',
      },
      { id: 'status', label: 'Status', sourceField: 'Status', type: 'status' },
      { id: 'purpose', label: 'Purpose', sourceField: 'Purpose of Prompt', type: 'longText' },
      { id: 'version', label: 'Version', sourceField: 'Version Number', type: 'number' },
      { id: 'promptType', label: 'Type', sourceField: 'Prompt Type', type: 'text' },
      { id: 'buildType', label: 'Build Type', sourceField: 'Prompt Build Type', type: 'text' },
      { id: 'agents', label: 'Agents', sourceField: 'Ai_Agents', type: 'text' },
    ],
  },
};

export function getManagedTableConfig(key: string): ManagedTableConfig | undefined {
  return MANAGED_TABLE_KEYS.includes(key as ManagedTableKey)
    ? MANAGED_TABLE_CONFIGS[key as ManagedTableKey]
    : undefined;
}

export function getManagedTableListFields(config: ManagedTableConfig): string[] {
  return Array.from(
    new Set([
      config.titleField,
      config.subtitleField,
      config.statusField,
      ...config.searchFields,
      ...config.columns.map((column) => column.sourceField),
    ].filter((field): field is string => Boolean(field))),
  );
}

/**
 * Fields a single record detail may expose. This is the list allow-list plus
 * any editable source fields, so the detail endpoint never returns raw or
 * unmapped Airtable fields to the UI.
 */
export function getManagedTableDetailFields(config: ManagedTableConfig): string[] {
  return Array.from(
    new Set([
      ...getManagedTableListFields(config),
      ...(config.editableFields ?? []).map((field) => field.sourceField),
    ]),
  );
}
